import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingBag,
  Briefcase,
  DollarSign,
  Clock,
  AlertTriangle,
  Plus,
  ArrowRight,
  RefreshCw,
  Package,
  TrendingUp,
  TrendingDown,
  Mail,
  MailOpen,
  Link2,
  Link2Off,
  Reply,
  Layers,
  Send,
  CalendarCheck,
  Factory,
  Users,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import MainNav from "@/components/MainNav";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

// Stat heading with a hover description tooltip
function StatLabel({ children, description, testid }) {
  return (
    <div className="flex items-center gap-1">
      <p className="text-aa-xs font-medium text-gray-500 uppercase tracking-wide">{children}</p>
      {description && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-gray-300 hover:text-gray-500 transition-colors cursor-help"
                data-testid={testid}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <Info className="w-3.5 h-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs">{description}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [inboxConnected, setInboxConnected] = useState(false);

  const getToken = () => localStorage.getItem("auth_token");

  const GMAIL_ACCOUNTS = [
    "info@alpinearmour.com.au",
    "scott@scottlistondesign.com.au"
  ];

  // Check if an email is awaiting reply (last message from external sender)
  const isAwaitingReply = (email) => {
    // Simple heuristic: if the email is in inbox and not sent by us
    const fromEmail = email.from?.toLowerCase() || "";
    return !GMAIL_ACCOUNTS.some(acc => fromEmail.includes(acc.toLowerCase()));
  };

  const fetchInboxData = async () => {
    const token = getToken();
    if (!token) return;
    
    setInboxLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Load account status and cached inbox data in parallel for a fast first paint
      const [accountsRes, inboxRes] = await Promise.all([
        axios.get(`${API}/gmail/accounts`, { headers }),
        axios.get(`${API}/gmail/inbox?filter=inbox&limit=6&cached=true`, { headers }).catch(() => ({ data: { emails: [] } })),
      ]);
      const accounts = accountsRes.data.accounts || [];
      const connected = accounts.filter(a => a.connected).length > 0;
      setInboxConnected(connected);
      if (connected) {
        setInboxEmails(inboxRes.data.emails || []);
      }
    } catch (error) {
      console.error("Failed to fetch inbox:", error);
    } finally {
      setInboxLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchInboxData();
  }, []);

  const fetchDashboardData = async () => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // Single consolidated stats call
      const statsRes = await axios.get(`${API}/dashboard/stats`, { headers });
      setStats(statsRes.data);

      // Recent jobs (last 5 non-draft) - still fetched separately for the recent list
      const jobsRes = await axios.get(`${API}/jobs`, { headers, params: { limit: 30 } });
      const allJobs = jobsRes.data?.jobs || jobsRes.data || [];
      const recent = allJobs
        .filter((j) => j.status !== "Draft")
        .sort(
          (a, b) =>
            new Date(b.updated_at || b.created_at) -
            new Date(a.updated_at || a.created_at)
        )
        .slice(0, 5);
      setRecentJobs(recent);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const startBusinessQuote = () => {
    navigate("/quote?new=business");
  };

  const startRetailSale = () => {
    navigate("/retail");
  };

  return (
    <WorkspaceLayout 
      title="Dashboard"
    >
      <main className="p-6">
        {/* Primary Stats — Business Quote Workflow */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Awaiting Client Approval */}
          <button
            onClick={() => navigate("/jobs?status=Sent")}
            data-testid="stat-awaiting-approval"
            className="text-left bg-aa-card rounded-aa-lg p-6 shadow-aa-card transition-all hover:-translate-y-0.5 hover:shadow-aa-card-hover"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <StatLabel description="Business quotes with status 'Sent' that the client hasn't approved yet." testid="stat-info-awaiting-approval">
                  Awaiting Approval
                </StatLabel>
                <p className="text-aa-xl font-bold mt-2 text-gray-900">
                  {stats?.awaiting_approval?.count ?? 0}
                </p>
                <p className="text-aa-xs text-gray-400 mt-1">
                  {stats?.awaiting_approval?.oldest_days
                    ? `Oldest: ${stats.awaiting_approval.oldest_days} days`
                    : "All caught up"}
                </p>
              </div>
              <div className="p-3 rounded-aa-md bg-amber-100 text-amber-700">
                <Send className="w-5 h-5" />
              </div>
            </div>
          </button>

          {/* Ready to Schedule */}
          <button
            onClick={() => navigate("/jobs?status=Approved")}
            data-testid="stat-ready-to-schedule"
            className="text-left bg-aa-card rounded-aa-lg p-6 shadow-aa-card transition-all hover:-translate-y-0.5 hover:shadow-aa-card-hover"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <StatLabel description="Approved jobs that are not yet in production — ready to be scheduled onto the production plan." testid="stat-info-ready-to-schedule">
                  Ready to Schedule
                </StatLabel>
                <p className="text-aa-xl font-bold mt-2 text-gray-900">
                  {stats?.ready_to_schedule?.count ?? 0}
                </p>
                <p className="text-aa-xs text-gray-400 mt-1">approved jobs</p>
              </div>
              <div className="p-3 rounded-aa-md bg-blue-100 text-blue-700">
                <CalendarCheck className="w-5 h-5" />
              </div>
            </div>
          </button>

          {/* In Production */}
          <button
            onClick={() => navigate("/jobs?status=In Production")}
            data-testid="stat-in-production"
            className="text-left bg-aa-card rounded-aa-lg p-6 shadow-aa-card transition-all hover:-translate-y-0.5 hover:shadow-aa-card-hover"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <StatLabel description="Jobs currently in production. The subtitle counts those due within the next 7 days." testid="stat-info-in-production">
                  In Production
                </StatLabel>
                <p className="text-aa-xl font-bold mt-2 text-gray-900">
                  {stats?.in_production?.count ?? 0}
                </p>
                <p className="text-aa-xs text-gray-400 mt-1">
                  {stats?.in_production?.due_next_7_days
                    ? `${stats.in_production.due_next_7_days} due ≤ 7 days`
                    : "no urgent deadlines"}
                </p>
              </div>
              <div className="p-3 rounded-aa-md bg-purple-100 text-purple-700">
                <Factory className="w-5 h-5" />
              </div>
            </div>
          </button>

          {/* Overdue */}
          <button
            onClick={() => navigate("/jobs?view=list&status=In Production")}
            data-testid="stat-overdue"
            className={`text-left rounded-aa-lg p-6 shadow-aa-card transition-all hover:-translate-y-0.5 hover:shadow-aa-card-hover ${
              (stats?.overdue?.count ?? 0) > 0 ? "bg-red-50 border-2 border-aa-error/30" : "bg-aa-card"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <StatLabel description="Approved or in-production jobs that are past their deadline." testid="stat-info-overdue">
                  Overdue
                </StatLabel>
                <p
                  className={`text-aa-xl font-bold mt-2 ${
                    (stats?.overdue?.count ?? 0) > 0 ? "text-aa-error" : "text-gray-900"
                  }`}
                >
                  {stats?.overdue?.count ?? 0}
                </p>
                <p className="text-aa-xs text-gray-400 mt-1">past deadline</p>
              </div>
              <div
                className={`p-3 rounded-aa-md ${
                  (stats?.overdue?.count ?? 0) > 0 ? "bg-aa-error/10 text-aa-error" : "bg-gray-100 text-gray-500"
                }`}
              >
                <AlertTriangle className="w-5 h-5" />
              </div>
            </div>
          </button>

          {/* This Week's Revenue */}
          <Link
            to="/settings"
            data-testid="stat-week-revenue"
            className="text-left bg-aa-card rounded-aa-lg p-6 shadow-aa-card transition-all hover:-translate-y-0.5 hover:shadow-aa-card-hover block"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <StatLabel description="Revenue (ex GST) from jobs marked Invoice Paid this week, with the week-on-week change. Only Invoice Paid jobs count toward revenue." testid="stat-info-week-revenue">
                  This Week Revenue
                </StatLabel>
                <p className="text-aa-xl font-bold mt-2 text-gray-900">
                  {formatCurrency(stats?.this_week_revenue?.amount)}
                </p>
                <p className="text-aa-xs mt-1 flex items-center gap-1">
                  {stats?.this_week_revenue?.wow_percent != null ? (
                    <>
                      {stats.this_week_revenue.wow_percent >= 0 ? (
                        <TrendingUp className="w-3 h-3 text-green-600" />
                      ) : (
                        <TrendingDown className="w-3 h-3 text-red-600" />
                      )}
                      <span className={stats.this_week_revenue.wow_percent >= 0 ? "text-green-600" : "text-red-600"}>
                        {stats.this_week_revenue.wow_percent >= 0 ? "+" : ""}
                        {stats.this_week_revenue.wow_percent}% WoW
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">vs last week —</span>
                  )}
                </p>
              </div>
              <div className="p-3 rounded-aa-md bg-aa-success/10 text-aa-success">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
          </Link>
        </div>

        {/* Secondary Stats — Today + Conversion */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-gray-50 border border-gray-100 rounded-aa-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <StatLabel description="Retail sales created today." testid="stat-info-retail-today">Retail Today</StatLabel>
                <p className="text-lg font-bold text-gray-900 mt-1">{stats?.retail_today ?? 0}</p>
              </div>
              <ShoppingBag className="w-4 h-4 text-aa-retail" />
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-aa-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <StatLabel description="Business quotes created today." testid="stat-info-business-today">Business Today</StatLabel>
                <p className="text-lg font-bold text-gray-900 mt-1">{stats?.business_today ?? 0}</p>
              </div>
              <Briefcase className="w-4 h-4 text-aa-primary" />
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-aa-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <StatLabel description="Revenue (ex GST) from jobs marked Invoice Paid today. Only Invoice Paid jobs count toward revenue." testid="stat-info-revenue-today">Revenue Today</StatLabel>
                <p className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(stats?.revenue_today)}</p>
              </div>
              <DollarSign className="w-4 h-4 text-aa-success" />
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-aa-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <StatLabel description="Month-to-date quote conversion: quotes won (Approved onward) divided by quotes sent." testid="stat-info-conversion">Conversion (MTD)</StatLabel>
                <p className="text-lg font-bold text-gray-900 mt-1">
                  {stats?.month_to_date?.conversion_rate ?? 0}%
                </p>
                <p className="text-aa-xs text-gray-400">
                  {stats?.month_to_date?.quotes_won ?? 0} of {stats?.month_to_date?.quotes_sent ?? 0} sent
                </p>
              </div>
              <TrendingUp className="w-4 h-4 text-purple-600" />
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-aa-lg p-4">
            <div className="flex items-center justify-between min-w-0">
              <div className="min-w-0 flex-1">
                <StatLabel description="Highest-revenue customer this month, based only on jobs marked Invoice Paid." testid="stat-info-top-customer">Top Customer (MTD)</StatLabel>
                <p className="text-sm font-bold text-gray-900 mt-1 truncate">
                  {stats?.month_to_date?.top_customer?.name || "—"}
                </p>
                <p className="text-aa-xs text-gray-400">
                  {formatCurrency(stats?.month_to_date?.top_customer?.revenue)}
                </p>
              </div>
              <Users className="w-4 h-4 text-amber-600 flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* Inbox Panel */}
        <div className="bg-aa-card rounded-aa-lg shadow-aa-card mb-8">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-aa-lg font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-aa-primary" />
              Recent Emails
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchInboxData}
                disabled={inboxLoading}
                className="p-2 rounded-aa-sm bg-aa-bg shadow-aa-button hover:-translate-y-0.5 transition-all active:shadow-aa-inset"
              >
                <RefreshCw className={`w-4 h-4 text-gray-500 ${inboxLoading ? "animate-spin" : ""}`} />
              </button>
              <Link to="/inbox">
                <Button variant="outline" size="sm">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
          
          <div className="divide-y divide-gray-100">
            {!inboxConnected ? (
              <div className="px-6 py-8 text-center">
                <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-3">Connect your Gmail accounts to see emails here</p>
                <Link to="/inbox">
                  <Button size="sm" className="bg-aa-primary">
                    <Plus className="w-4 h-4 mr-1" />
                    Connect Gmail
                  </Button>
                </Link>
              </div>
            ) : inboxLoading ? (
              <div className="px-6 py-8 text-center">
                <RefreshCw className="w-6 h-6 text-gray-400 mx-auto animate-spin" />
              </div>
            ) : inboxEmails.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500 text-sm">
                No emails in inbox
              </div>
            ) : (
              inboxEmails.map((email) => {
                const isUnread = email.is_unread;
                const awaiting = isAwaitingReply(email);
                const isLinked = !!email.linked_job_id;
                const senderName = email.from?.match(/^([^<]+)/)?.[1]?.trim()?.replace(/"/g, "") || email.from || "";
                
                return (
                  <Link
                    key={email.thread_id}
                    to={`/inbox?thread=${email.thread_id}&account=${encodeURIComponent(email.account_email)}`}
                    className={`flex items-center gap-4 px-6 py-3 hover:bg-gray-50 transition-colors ${isUnread ? "bg-blue-50/30" : ""}`}
                  >
                    {/* Unread indicator */}
                    <div className="w-2 shrink-0">
                      {isUnread && <div className="w-2 h-2 rounded-full bg-aa-primary" />}
                    </div>
                    
                    {/* Sender avatar */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-medium ${
                      GMAIL_ACCOUNTS.some(a => email.from?.toLowerCase().includes(a.toLowerCase()))
                        ? "bg-aa-primary text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}>
                      {senderName.charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Email content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm truncate ${isUnread ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                          {senderName}
                        </span>
                        {awaiting && (
                          <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">
                            <Reply className="w-2.5 h-2.5 mr-0.5 rotate-180" />
                            Reply
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm truncate ${isUnread ? "font-medium text-gray-800" : "text-gray-600"}`}>
                        {email.subject || "(No Subject)"}
                      </p>
                    </div>
                    
                    {/* Link status & Account */}
                    <div className="flex items-center gap-2 shrink-0">
                      {isLinked ? (
                        <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                          <Link2 className="w-2.5 h-2.5 mr-0.5" />
                          {email.linked_job_number}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-gray-400">
                          <Link2Off className="w-2.5 h-2.5 mr-0.5" />
                          Unlinked
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px]">
                        {email.account_email?.split("@")[0]}
                      </Badge>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>

        {/* Quick Links & Recent Jobs */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quick Actions Card */}
          <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card">
            <h2 className="text-aa-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-aa-primary" />
              Quick Actions
            </h2>
            <div className="space-y-3">
              <button
                onClick={startRetailSale}
                className="w-full flex items-center justify-between p-3 bg-aa-bg rounded-aa-md shadow-aa-input hover:shadow-none transition-all text-left"
              >
                <span className="flex items-center gap-2 text-aa-sm font-medium text-gray-700">
                  <ShoppingBag className="w-4 h-4 text-aa-retail" />
                  New Retail Sale
                </span>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </button>
              <button
                onClick={startBusinessQuote}
                className="w-full flex items-center justify-between p-3 bg-aa-bg rounded-aa-md shadow-aa-input hover:shadow-none transition-all text-left"
              >
                <span className="flex items-center gap-2 text-aa-sm font-medium text-gray-700">
                  <Briefcase className="w-4 h-4 text-aa-primary" />
                  New Business Quote
                </span>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </button>
              <Link to="/jobs" className="block">
                <div className="w-full flex items-center justify-between p-3 bg-aa-bg rounded-aa-md shadow-aa-input hover:shadow-none transition-all">
                  <span className="flex items-center gap-2 text-aa-sm font-medium text-gray-700">
                    <Package className="w-4 h-4 text-purple-500" />
                    View All Jobs
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </Link>
              <Link to="/products" className="block">
                <div className="w-full flex items-center justify-between p-3 bg-aa-bg rounded-aa-md shadow-aa-input hover:shadow-none transition-all">
                  <span className="flex items-center gap-2 text-aa-sm font-medium text-gray-700">
                    <Package className="w-4 h-4 text-green-500" />
                    Product Library
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </Link>
            </div>
          </div>

          {/* Recent Activity Card */}
          <div className="bg-aa-card rounded-aa-lg p-6 shadow-aa-card lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-aa-lg font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-aa-primary" />
                Recent Activity
              </h2>
              <button
                onClick={fetchDashboardData}
                disabled={loading}
                className="p-2 rounded-aa-sm bg-aa-bg shadow-aa-button hover:-translate-y-0.5 transition-all active:shadow-aa-inset"
              >
                <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-8 text-gray-500 text-aa-sm">
                Loading...
              </div>
            ) : recentJobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-aa-sm">
                No recent jobs
              </div>
            ) : (
              <div className="space-y-2">
                {recentJobs.map((job) => (
                  <Link
                    key={job.id}
                    to={`/quote?job=${job.id}`}
                    className="flex items-center justify-between p-3 rounded-aa-md bg-aa-bg hover:shadow-aa-input transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-1 bg-white rounded-aa-sm text-aa-xs font-mono text-gray-600 shadow-sm">
                        {job.job_number}
                      </span>
                      <div>
                        <p className="font-medium text-aa-sm text-gray-900 line-clamp-1">
                          {job.job_name || "Untitled Job"}
                        </p>
                        <p className="text-aa-xs text-gray-500">
                          {job.job_data?.client?.business_name || job.job_data?.client?.contact_name || "No client"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {job.job_data?.client_type === "Retail" && (
                        <span className="px-2 py-1 bg-aa-retail/10 text-aa-retail text-aa-xs font-medium rounded-aa-sm">
                          Retail
                        </span>
                      )}
                      <span className={`px-2 py-1 text-aa-xs font-medium rounded-aa-sm ${
                        job.status === "Complete" ? "bg-aa-success/10 text-aa-success" :
                        job.status === "In Production" ? "bg-aa-warning/10 text-aa-warning" :
                        job.status === "Approved" ? "bg-green-100 text-green-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>
                        {job.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </WorkspaceLayout>
  );
}
